import React, { useState, useMemo } from 'react';
import { money, num, unitOf, rateLabel } from './tsFormat';

/* Ведомость: кому сколько платить. Табель отвечает на вопрос «сколько
   отработал», ведомость — «сколько отдать». Зарплату в бане выдают дважды:
   середина месяца (за 1–15) и конец (за 16–N плюс доплаты минус штрафы),
   поэтому у ведомости три вида — весь месяц и каждая выплата отдельно,
   с колонкой под подпись на бумаге. */

const MODES = [
  { key: 'all', label: 'Весь месяц' },
  { key: 'first', label: 'Первая выплата' },
  { key: 'second', label: 'Вторая выплата' },
];

const PayoutSheet = ({ data }) => {
  const { period, entries, days_in_month: n } = data;
  const [mode, setMode] = useState('all');
  const [hideZero, setHideZero] = useState(true);

  const amountOf = (e) => (mode === 'first' ? e.advance1
    : mode === 'second' ? e.advance2 + e.bonus - e.fines : e.total);

  const sections = useMemo(() => {
    const out = [];
    entries.forEach(entry => {
      if (hideZero && !amountOf(entry)) return;
      let section = out[out.length - 1];
      if (!section || section.position !== entry.position) {
        section = { position: entry.position, rows: [] };
        out.push(section);
      }
      section.rows.push(entry);
    });
    return out;
    // amountOf зависит только от mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, hideZero, mode]);

  const sum = (rows, pick) => rows.reduce((a, e) => a + pick(e), 0);
  const all = sections.flatMap(s => s.rows);
  const title = mode === 'first' ? `за 1–15 ${period.label.toLowerCase()}`
    : mode === 'second' ? `за 16–${n} ${period.label.toLowerCase()}` : `за ${period.label.toLowerCase()}`;

  const columns = mode === 'all'
    ? ['Оплата', 'Отработано', 'За 1–15', `За 16–${n}`, 'Доплаты', 'Штрафы', 'Итого']
    : mode === 'first' ? ['Оплата', 'К выплате']
      : ['Оплата', `За 16–${n}`, 'Доплаты', 'Штрафы', 'К выплате'];

  const cells = (e) => {
    if (mode === 'all') {
      return [rateLabel(e.pay_type, e.rate), `${num(e.hours)} ${unitOf(e.pay_type, e.hours)}`,
        money(e.advance1), money(e.advance2), e.bonus ? money(e.bonus) : '', e.fines ? `−${money(e.fines)}` : '',
        money(e.total)];
    }
    if (mode === 'first') return [rateLabel(e.pay_type, e.rate), money(e.advance1)];
    return [rateLabel(e.pay_type, e.rate), money(e.advance2), e.bonus ? money(e.bonus) : '',
      e.fines ? `−${money(e.fines)}` : '', money(e.advance2 + e.bonus - e.fines)];
  };

  const totals = (rows) => {
    if (mode === 'all') {
      return ['', '', money(sum(rows, e => e.advance1)), money(sum(rows, e => e.advance2)),
        money(sum(rows, e => e.bonus)), money(sum(rows, e => e.fines)), money(sum(rows, e => e.total))];
    }
    if (mode === 'first') return ['', money(sum(rows, e => e.advance1))];
    return ['', money(sum(rows, e => e.advance2)), money(sum(rows, e => e.bonus)),
      money(sum(rows, e => e.fines)), money(sum(rows, e => e.advance2 + e.bonus - e.fines))];
  };

  return (
    <div className="ts-payout">
      <div className="ts-toolbar ts-no-print">
        <div className="ts-segment" role="tablist" aria-label="Какая выплата">
          {MODES.map(m => (
            <button key={m.key} type="button" role="tab" aria-selected={mode === m.key}
              className={mode === m.key ? 'is-on' : ''} onClick={() => setMode(m.key)}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="ts-check">
          <input type="checkbox" checked={hideZero} onChange={() => setHideZero(v => !v)} />
          Скрыть строки без суммы
        </label>
        <button type="button" className="ui-btn-primary ts-print-btn" onClick={() => window.print()}>
          Печать
        </button>
      </div>

      <div className="ts-sheet">
        <div className="ts-sheet-head">
          <h2>Ведомость {title}</h2>
          <span className="ts-sheet-total">{money(sum(all, amountOf))}</span>
        </div>
        <table className="ts-sheet-table">
          <thead>
            <tr>
              <th scope="col">Сотрудник</th>
              {columns.map(c => <th scope="col" key={c} className={c === 'Оплата' ? '' : 'ts-num-h'}>{c}</th>)}
              <th scope="col" className="ts-sign">Подпись</th>
            </tr>
          </thead>
          {sections.map(section => (
            <tbody key={section.position}>
              <tr className="ts-sheet-group">
                <th scope="rowgroup" colSpan={columns.length + 2}>{section.position}</th>
              </tr>
              {section.rows.map(e => (
                <tr key={e.id}>
                  <th scope="row">{e.full_name}</th>
                  {cells(e).map((value, i) => (
                    <td key={columns[i]} className={i === 0 ? 'ts-pay' : 'ts-num'}>
                      {value}
                    </td>
                  ))}
                  <td className="ts-sign" />
                </tr>
              ))}
              {section.rows.length > 1 && (
                <tr className="ts-sheet-sub">
                  <th scope="row">Итого по группе</th>
                  {totals(section.rows).map((v, i) => <td key={columns[i]} className={i === 0 ? '' : 'ts-num'}>{v}</td>)}
                  <td className="ts-sign" />
                </tr>
              )}
            </tbody>
          ))}
          <tfoot>
            <tr>
              <th scope="row">Всего</th>
              {totals(all).map((v, i) => <td key={columns[i]} className={i === 0 ? '' : 'ts-num'}>{v}</td>)}
              <td className="ts-sign" />
            </tr>
          </tfoot>
        </table>
        {all.length === 0 && <p className="ts-empty">В этом месяце нечего выплачивать.</p>}
      </div>
    </div>
  );
};

export default PayoutSheet;
